import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { api, setAuthToken, subscribeUnauthorized } from '../services/api.js';

/**
 * AuthContext — the single source of truth for the real (non-demo) auth
 * session. Demo mode (?demo=true) bypasses this context entirely and falls
 * back to RoleContext, so this provider is only consulted when a real JWT
 * is present.
 *
 * Shape:
 *   { token, user, login({email,password}), logout(), isAuthenticated,
 *     isLoading }
 *
 * On mount: if a token is in localStorage, GET /auth/me to validate it.
 * If the backend rejects it, we clear the token silently.
 */

const STORAGE_KEY = 'atlacis_jwt';

const AuthContext = createContext(null);

function readStoredToken() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredToken(token) {
  if (typeof window === 'undefined') return;
  try {
    if (token) window.localStorage.setItem(STORAGE_KEY, token);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => readStoredToken());
  const [user, setUser] = useState(null);
  // Only true while we're revalidating a stored token on first load.
  const [isLoading, setIsLoading] = useState(() => Boolean(readStoredToken()));

  // Keep the axios client and localStorage in sync with React state.
  useEffect(() => {
    setAuthToken(token);
    writeStoredToken(token);
  }, [token]);

  // On mount / when token changes to a new value: validate via /auth/me.
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setUser(null);
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      setIsLoading(true);
      try {
        const { data } = await api.get('/auth/me');
        if (!cancelled) setUser(data.user);
      } catch {
        if (!cancelled) {
          setUser(null);
          setToken(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // If any authenticated request returns 401, force-logout.
  useEffect(() => {
    return subscribeUnauthorized(() => {
      setUser(null);
      setToken(null);
    });
  }, []);

  const login = useCallback(async ({ email, password }) => {
    const { data } = await api.post('/auth/login', { email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  // External token adoption (Google OAuth callback). Stores the token so
  // the /me validation useEffect fetches the profile and flips
  // isAuthenticated once both are present.
  const loginWithToken = useCallback(async (newToken) => {
    if (!newToken) throw new Error('Missing token');
    setToken(newToken);
    setAuthToken(newToken);
    const { data } = await api.get('/auth/me');
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
  }, []);

  const value = useMemo(
    () => ({
      token,
      user,
      login,
      loginWithToken,
      logout,
      isAuthenticated: Boolean(token && user),
      isLoading,
    }),
    [token, user, login, loginWithToken, logout, isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthSession() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthSession must be used inside <AuthProvider>');
  return ctx;
}
